/**
 * v0.9.234: NMI payment client for the frontend.
 *
 * Collect.js is NMI's tokenization library. It injects an iframe hosted
 * on secure.nmi.com so card data is entered there directly - your domain
 * never sees the PAN. Returns a one-time `payment_token` you POST to
 * your backend to charge.
 *
 * Three usage patterns:
 *   - INLINE FIELDS: Collect.js renders 3 fields (number, expiry, cvv)
 *     on your form. Most flexibility, most PCI scope still SAQ A.
 *   - LIGHTBOX: Collect.js renders a button that opens an NMI-hosted
 *     payment modal. Easiest to integrate; less branded.
 *   - APPLE/GOOGLE PAY: Collect.js handles the wallet flow.
 *
 * This module exposes a Promise-based wrapper around either approach.
 *
 * Lifecycle:
 *   1. await ensureCollectJsLoaded(tokenizationKey)  -- injects <script>
 *   2. await configureCollect({ ... callbacks })     -- wires fields
 *   3. await tokenize()                              -- returns payment_token
 *   4. POST { payment_token } to /v1/payments/nmi/sale
 */

import { api } from './api';

declare global {
  interface Window {
    CollectJS?: CollectJSGlobal;
  }
}

type CollectJSGlobal = {
  configure: (opts: CollectJSConfigure) => void;
  startPaymentRequest: () => void;
};

type CollectJSConfigure = {
  variant?: 'inline' | 'lightbox';
  paymentSelector?: string;
  fields?: {
    ccnumber?: { selector: string; placeholder?: string };
    ccexp?: { selector: string; placeholder?: string };
    cvv?: { selector: string; placeholder?: string };
  };
  callback: (response: CollectJSResponse) => void;
  /** v0.9.706: BIN card-type lookup. Fires as soon as the typed card
   *  number is long enough to identify; info.cardCategory is
   *  'credit' | 'debit' | 'prepaid' | ... or absent when unknown. */
  lookUpCallback?: (info: { cardCategory?: string; category?: string; [k: string]: unknown }) => void;
  validationCallback?: (field: string, status: boolean, message: string) => void;
  fieldsAvailableCallback?: () => void;
  styleSniffer?: boolean;
  // Color / styling overrides for the iframe-rendered fields:
  customCss?: Record<string, string>;
  focusCss?: Record<string, string>;
  invalidCss?: Record<string, string>;
};

export type CollectJSResponse = {
  token: string;
  card: {
    number: string;       // masked, e.g. "411111******1111"
    bin: string;
    exp: string;
    type: string;         // "visa" | "mastercard" | ...
    /** v0.9.706: funding category from NMI's BIN data, when present.
     *  The authoritative value for surcharge decisions. */
    category?: string;
  };
  check?: unknown;
  tokenType: string;
};

let scriptLoadPromise: Promise<void> | null = null;
let lastLoadedKey: string | null = null;

/**
 * Collect.js is a static script served ONLY from secure.nmi.com - for BOTH
 * sandbox and production. Sandbox vs production is determined by the
 * tokenization KEY, not the host (per NMI docs; every official example,
 * including the sandbox quick-start, loads secure.nmi.com/token/Collect.js).
 *
 * IMPORTANT: this is the OPPOSITE of the backend Direct Connect API, which
 * DOES host-split (sandbox.nmi.com vs secure.nmi.com). An earlier version
 * wrongly mirrored that split here and loaded Collect.js from
 * sandbox.nmi.com/token/Collect.js - which loads but never mounts the iframe
 * fields, so the card form hangs on "Loading secure card form..." forever in
 * sandbox. Both hosts point at the same script; keep this at secure.nmi.com.
 */
const COLLECTJS_HOSTS = {
  production: 'https://secure.nmi.com/token/Collect.js',
  sandbox: 'https://secure.nmi.com/token/Collect.js',
} as const;

/**
 * Inject the Collect.js script tag. Idempotent per key. If the
 * tokenization key changes (e.g. switching tenant), reloads.
 *
 * @param tokenizationKey  the public Collect.js key
 * @param sandbox          load from the sandbox gateway host when true
 */
export async function ensureCollectJsLoaded(
  tokenizationKey: string,
  sandbox: boolean,
): Promise<void> {
  // v0.9.709: sanitize. A key saved with stray whitespace/newline from
  // mobile copy-paste makes Collect.js silently refuse to initialize.
  tokenizationKey = tokenizationKey.trim();
  if (scriptLoadPromise && lastLoadedKey === tokenizationKey) {
    return scriptLoadPromise;
  }
  // v0.9.708: the tokenization key CHANGED (admin re-keyed NMI). The old
  // Collect.js script is permanently bound to the old key and refuses to
  // re-initialize - leaving it produces the dreaded "failed to expose
  // window.CollectJS" until a full app restart. Tear it out and load
  // fresh with the new key instead.
  if (lastLoadedKey !== null && lastLoadedKey !== tokenizationKey) {
    document.querySelectorAll('#nt-collectjs, script[data-collectjs]').forEach((el) => el.remove());
    try { delete (window as { CollectJS?: unknown }).CollectJS; } catch (_) { (window as { CollectJS?: unknown }).CollectJS = undefined; }
    scriptLoadPromise = null;
  }
  lastLoadedKey = tokenizationKey;
  // v0.9.710: never cache a FAILURE. Previously one transient load
  // failure poisoned the session - the rejected promise was cached and
  // every retry replayed it without touching the network. On failure,
  // clear the cache and remove the dead script tag so the next attempt
  // genuinely retries.
  const attempt = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('#nt-collectjs, script[data-collectjs]');
    if (existing && window.CollectJS) {
      resolve();
      return;
    }
    // v0.9.1042: a script tag already exists but window.CollectJS is not yet
    // exposed. Do NOT inject a second tag - Collect.js refuses to expose itself
    // when loaded twice on one page (loads fine, but window.CollectJS never
    // appears -> "failed to initialize"). This is the reopen-the-form case.
    // Instead, tear out the stale tag and clear the cached promise so the fresh
    // injection below is the ONLY Collect.js on the page.
    if (existing) {
      existing.remove();
      try { delete (window as { CollectJS?: unknown }).CollectJS; } catch (_) { (window as { CollectJS?: unknown }).CollectJS = undefined; }
    }
    const s = document.createElement('script');
    s.src = sandbox ? COLLECTJS_HOSTS.sandbox : COLLECTJS_HOSTS.production;
    s.async = true;
    // v0.9.1051: the tag carries ONLY data-tokenization-key. EMPIRICALLY PROVEN
    // on this exact device/key/origin: the bare /nmi-test.html page injects the
    // tag with just the key attribute and fields MOUNT (screenshot-verified,
    // post-CSP-fix), while the app loader carrying data-variant +
    // data-collectjs failed with "window.CollectJS never appeared". This
    // matches the original v0.9.716 empirical finding: extra data-* attributes
    // on the Collect.js tag make it load but silently never expose itself.
    // (v0.9.1041 wrongly re-added data-collectjs based on a diff against 679,
    // a build never actually confirmed working.) inline variant is declared in
    // CollectJS.configure(), which the bare page proved is sufficient.
    // Tracking uses the id attribute (not data-*), which the bare page's
    // success shows Collect.js tolerates fine via configure-driven setup.
    s.setAttribute('data-tokenization-key', tokenizationKey);
    s.id = 'nt-collectjs';
    s.onload = () => {
      // Collect.js may assign window.CollectJS a tick AFTER onload fires,
      // so poll briefly rather than checking once synchronously.
      const start = Date.now();
      const poll = () => {
        if (window.CollectJS) {
          resolve();
          return;
        }
        if (Date.now() - start > 8000) {
          const fp = tokenizationKey.length + ' chars, ' + tokenizationKey.split('-').length + ' groups, ' + tokenizationKey.slice(0, 3) + '...' + tokenizationKey.slice(-3);
          // v0.9.715: what NMI traffic actually happened? Lists every
          // nmi.com resource the page fetched (the Collect.js script
          // itself plus any internal validation calls it attempted).
          let net = 'n/a';
          try {
            net = performance.getEntriesByType('resource')
              .filter((r) => r.name.includes('nmi.com'))
              .map((r) => r.name.replace(/^https:\/\//, '').slice(0, 60) + ' (' + Math.round(r.duration) + 'ms)')
              .join(' ; ') || 'NO nmi.com requests recorded';
          } catch (_) { /* ignore */ }
          reject(new Error('Payment form could not initialize. Key the app used: [' + fp + '] on ' + s.src + '. Compare those first/last characters against the key on the NMI public keys page - if they differ, re-save the key in Admin > Payments. NMI network activity: [' + net + ']'));
          return;
        }
        setTimeout(poll, 100);
      };
      poll();
    };
    s.onerror = () => reject(new Error('Failed to load Collect.js from ' + s.src));
    document.head.appendChild(s);
  });
  scriptLoadPromise = attempt.catch((e) => {
    scriptLoadPromise = null;
    lastLoadedKey = null;
    document.querySelectorAll('#nt-collectjs, script[data-collectjs]').forEach((el) => el.remove());
    throw e;
  });
  return scriptLoadPromise;
}

/**
 * Configure Collect.js for inline-field mode and return a tokenize()
 * function that resolves when the user submits valid card data.
 *
 * Caller passes three div selectors where the iframe fields render.
 * After configure() returns, the user types card data, then your form
 * triggers tokenize().
 */
export type InlineFieldsConfig = {
  ccnumberSelector: string;
  ccexpSelector: string;
  cvvSelector: string;
};

/** Normalize NMI's card category strings to our four buckets.
 *  ANYTHING unrecognized is 'unknown', and unknown is never surcharged
 *  (NMI guidance + fail-safe). */
export type NmiCardCategory = 'credit' | 'debit' | 'prepaid' | 'unknown';
export function normalizeCardCategory(raw: unknown): NmiCardCategory {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (v === 'credit') return 'credit';
  if (v === 'debit') return 'debit';
  if (v === 'prepaid') return 'prepaid';
  return 'unknown';
}

export function configureInlineFields(
  cfg: InlineFieldsConfig,
  onReady?: () => void,
  onCardLookup?: (category: NmiCardCategory) => void,
): { tokenize: () => Promise<CollectJSResponse> } {
  if (!window.CollectJS) {
    throw new Error('Collect.js not loaded - call ensureCollectJsLoaded first');
  }

  let resolver: ((r: CollectJSResponse) => void) | null = null;
  let rejecter: ((e: Error) => void) | null = null;
  const fieldErrors = new Map<string, string>();

  window.CollectJS.configure({
    variant: 'inline',
    fields: {
      ccnumber: { selector: cfg.ccnumberSelector, placeholder: 'Card number' },
      ccexp: { selector: cfg.ccexpSelector, placeholder: 'MM/YY' },
      cvv: { selector: cfg.cvvSelector, placeholder: 'CVV' },
    },
    // Fires once the iframe fields have actually mounted into the DOM.
    // This is the real "ready" signal - configure() returning is NOT
    // enough; the iframes mount asynchronously after it.
    fieldsAvailableCallback: () => {
      onReady?.();
    },
    // v0.9.706: live BIN lookup. Fires while the customer is still
    // typing, and AGAIN if they clear the field and enter a different
    // card - so the surcharge preview appears and disappears with the
    // actual card type. The tokenization callback's card.category is
    // still the authoritative value at submit.
    lookUpCallback: (info) => {
      try { console.info('[nmi] BIN lookup:', JSON.stringify(info)); } catch (_) { /* ignore */ }
      // v0.9.712: NMI's actual field name is `category` (proven by live
      // BIN lookup payloads); `cardCategory` kept as a fallback for any
      // account that sends the documented name.
      onCardLookup?.(normalizeCardCategory(info?.category ?? info?.cardCategory));
    },
    callback: (response) => {
      if (resolver) {
        resolver(response);
        resolver = null;
        rejecter = null;
      }
    },
    validationCallback: (field, status, msg) => {
      // v0.9.718: remember the latest invalid-field message so a
      // tokenization timeout can report Collect.js's actual complaint
      // (e.g. a card brand the merchant account doesn't accept) instead
      // of a mute "timed out". Only used when a field is genuinely
      // invalid at timeout - a clean timeout still says timed out.
      if (status) {
        fieldErrors.delete(field);
      } else {
        fieldErrors.set(field, msg || 'invalid');
      }
    },
  });

  return {
    tokenize: () => new Promise<CollectJSResponse>((resolve, reject) => {
      resolver = resolve;
      rejecter = reject;
      try {
        window.CollectJS!.startPaymentRequest();
      } catch (e) {
        rejecter?.(e as Error);
        resolver = null;
        rejecter = null;
      }
      // Timeout safety: 30s
      setTimeout(() => {
        if (rejecter) {
          if (fieldErrors.size > 0) {
            const details = Array.from(fieldErrors.entries())
              .map(([f, m]) => f + ': ' + m)
              .join('; ');
            rejecter(new Error('Card not accepted - ' + details + '. (If the card number is correct, this brand may not be enabled on the merchant account.)'));
          } else {
            rejecter(new Error('Tokenization timed out'));
          }
          resolver = null;
          rejecter = null;
        }
      }, 30_000);
    }),
  };
}

// ============================================================================
// Payment API calls - wraps the backend routes
// ============================================================================

export type PaymentResult = {
  ok: boolean;
  id: string;
  nmiTransactionId?: string;
  responseCode: string;
  responseText: string;
  reason?: 'declined' | 'errored';
  last4?: string;
  cardBrand?: string;
  authCode?: string;
  customerVaultId?: string;
  /** v0.9.2072 (backend 1417): what happened to a tipLater authorization -
   *  'registered' = will be captured with its tip. Absent = an older backend
   *  that did not register it (the POS then captures it itself). */
  tipCapture?: 'registered' | 'captured-now' | 'uncaptured';
};

export type PaymentConfig = {
  configured: boolean;
  tokenizationKey?: string;
  sandbox?: boolean;
};

export async function fetchPaymentConfig(): Promise<PaymentConfig> {
  return api<PaymentConfig>('/v1/payments/nmi/config', { auth: 'user-access' });
}

export async function nmiSale(args: {
  /** Everything charged: bill + surcharge + tip. */
  amountCents: number;
  /** v0.9.2071: the tip inside amountCents, sent to NMI as `tip` (backend 1416). */
  tipCents?: number;
  paymentToken?: string;
  customerVaultId?: string;
  orderId?: string;
  email?: string;
  addToVault?: boolean;
  /** v0.9.1349: 0 disables NMI's duplicate-transaction window for this
   *  request. See the client comment - a bar tab legitimately repeats. */
  dupSeconds?: number;
}): Promise<PaymentResult> {
  return api<PaymentResult>('/v1/payments/nmi/sale', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}

export async function nmiAuth(args: {
  amountCents: number;
  paymentToken?: string;
  customerVaultId?: string;
  orderId?: string;
  email?: string;
  /** v0.9.1343: keep the card at the gateway during the authorisation.
   *  Collect.js tokens are single-use, so this is the only opportunity -
   *  after the auth returns, the card is gone unless it was vaulted. */
  addToVault?: boolean;
  /** v0.9.1349: 0 disables NMI's duplicate-transaction window for this
   *  request. See the client comment - a bar tab legitimately repeats. */
  dupSeconds?: number;
  /** v0.9.2072: the tip inside amountCents (entered at payment). */
  tipCents?: number;
  /** v0.9.2072: a POS card payment whose tip may be added later - captured
   *  with the tip, or by the server before the batch (backend 1417). */
  tipLater?: boolean;
}): Promise<PaymentResult> {
  return api<PaymentResult>('/v1/payments/nmi/auth', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}

export async function nmiCapture(args: {
  nmiTransactionId: string;
  amountCents: number;
  tipCents?: number;
  orderId?: string;
}): Promise<PaymentResult> {
  return api<PaymentResult>('/v1/payments/nmi/capture', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}

export async function nmiVoid(args: {
  nmiTransactionId: string;
  orderId?: string;
}): Promise<PaymentResult> {
  return api<PaymentResult>('/v1/payments/nmi/void', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}

export async function nmiRefund(args: {
  nmiTransactionId: string;
  amountCents: number;
  orderId?: string;
}): Promise<PaymentResult> {
  return api<PaymentResult>('/v1/payments/nmi/refund', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}

export interface ReconcileResult {
  action: 'none' | 'refund' | 'charge';
  moved: boolean;
  preview?: boolean;
  blocked?: 'NO_VAULT';
  detail?: string | null;
  deltaCents: number;
  netCapturedCents: number;
  newTotalCents: number;
  last4: string | null;
  cardBrand: string | null;
  hasVault: boolean;
  ok?: boolean;
  responseText?: string;
  reason?: 'declined' | 'errored';
}

// v0.9.748: reconcile an already-paid online order's card charge against a new
// total after a POS edit. preview:true computes the amount WITHOUT moving money
// (for the confirm dialog); omit it to actually refund/charge.
export interface TipAdjustResult {
  ok: boolean;
  id: string;
  /** v0.9.2072: 'scheduled' = saved; goes on the card with the check before the batch. */
  mode: 'capture' | 'update' | 'scheduled';
  baseCents: number;
  tipCents: number;
  newTotalCents: number;
  last4: string | null;
  cardBrand: string | null;
  nmiTransactionId?: string;
  nmiAnchorTransactionId?: string;
  responseCode: string;
  responseText: string;
  reason?: 'declined' | 'errored';
}

// v0.9.829: push a tip-on-receipt adjustment to NMI. The backend picks the
// right gateway op (capture an auth, or update an unsettled captured sale).
// tipCents is absolute (replaces any prior tip on the charge).
export async function nmiTipAdjust(args: {
  orderId: string;
  tipCents: number;
  /**
   * v0.9.1857: the card's own gateway transaction (tender.reference). On a
   * check paid with more than one card, this picks the right charge; without
   * it the backend anchors to the order's first charge (unchanged).
   */
  nmiTransactionId?: string;
}): Promise<TipAdjustResult> {
  return api<TipAdjustResult>('/v1/payments/nmi/tip-adjust', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}

export interface BatchSummary {
  count: number;
  refundCount: number;
  capturedCents: number;
  tipCents: number;
  refundedCents: number;
  netCents: number;
  openedAt: string | null;
}

// v0.9.834: open-batch summary from our recorded NMI transactions.
export async function fetchNmiBatch(): Promise<BatchSummary> {
  return api<BatchSummary>('/v1/payments/nmi/batch', { auth: 'user-access' });
}

export interface ReplaySettlementsResult {
  ok: boolean;
  replayedEvents: number;
  settledTransactions: number;
}

// v0.9.836: replay stored settlement webhooks that never stamped settled_at,
// clearing stale open transactions NMI already settled.
export async function replayNmiSettlements(): Promise<ReplaySettlementsResult> {
  return api<ReplaySettlementsResult>('/v1/admin/payments/nmi/replay-settlements', {
    method: 'POST',
    auth: 'user-access',
  });
}

export interface MarkOpenSettledResult {
  ok: boolean;
  markedCount: number;
  settledAt: string;
}

// v0.9.837: one-time LOCAL reset -- mark all currently-open transactions
// settled in our records (does NOT touch NMI). For pre-webhook backlog already
// settled at the gateway.
export async function markNmiOpenSettled(): Promise<MarkOpenSettledResult> {
  return api<MarkOpenSettledResult>('/v1/admin/payments/nmi/mark-open-settled', {
    method: 'POST',
    auth: 'user-access',
  });
}

export async function nmiReconcile(args: {
  orderId: string;
  newTotalCents: number;
  preview?: boolean;
}): Promise<ReconcileResult> {
  return api<ReconcileResult>('/v1/payments/nmi/reconcile', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}

/**
 * v0.9.1130: settle the outstanding difference on an already-paid online order
 * in CASH. Records a cash tender on the order (no gateway call), which clears
 * the balance-due banner the same way a card charge does.
 */
export async function nmiSettleCash(args: {
  orderId: string;
  newTotalCents: number;
  amountCents: number;
  tenderedCents?: number;
}): Promise<{ ok: boolean; moved: boolean; action: string; amountCents: number; changeCents: number }> {
  return api('/v1/payments/nmi/settle-cash', {
    method: 'POST',
    auth: 'user-access',
    body: args,
  });
}
